import React, {useState, useEffect} from 'react';
import {Redirect} from 'react-router-dom'
import {Translation, getI18n} from 'react-i18next';
import {Affix, Button, Card, Checkbox, Col, DatePicker, Form, Input, Row, Spin} from 'antd';
import {LoadingOutlined, PlusOutlined} from '@ant-design/icons';
import moment from 'moment';

import BodyInput from './BodyInput';
import RecipientInputRow from './RecipientInputRow';
import PublicStatusInput from './PublicStatusInput';
import MembersInput from '../../careGuideHelpers/containers/MembersInputContainer';
import message from '../../../elements/lib/MessageWrapper';
import FormMetadata from '../../../elements/components/FormMetadata';
import {pathTo} from '../../../Routes';
import Format from '../../../../../lib/Format';
import Logger from '../../../../../lib/Logger';
import Config from '../../../../../Config';


const MessageForm = ({id, data, errors, load, destroyForm, isLoading, isSubmitting, created_id, ...props}) => {

  const [redirectTo, setRedirectTo] = useState(null);
  const [careGuideId, setCareGuideId] = useState(data['care_guide']);
  const [form] = Form.useForm();

  // form column settings
  const layout = {
    main: {
      labelCol: {span: 5},
      wrapperCol: {span: 19},
    },
  }

  // load record data from API
  useEffect(() => {
    if (id) {
      load(id);
    }
  }, [id, load]);

  // update input values when new data is available
  const dataString = JSON.stringify(data);
  useEffect(() => {
    if (id && !isSubmitting) {
      const dataObj = JSON.parse(dataString);
      dataObj['sent_at'] = dataObj['sent_at']
        ? moment(dataObj['sent_at'], Config.get('API_DATETIME_FORMAT'))
        : null;
      form.setFieldsValue(dataObj);
      setCareGuideId(dataObj['care_guide']);
    }
  }, [form, dataString, isSubmitting, id]);

  // handle errors reported by API
  useEffect(() => {
    let firstFieldName = '';
    for (const field in errors) {
      form.setFields([{name: field, errors: errors[field]}]);
      if (firstFieldName === '') {
        firstFieldName = field;
      }
    }
    form.scrollToField(firstFieldName);
  }, [form, errors]);

  // redirect add form to edit form on successful create action
  useEffect(() => {
    if (created_id) {
      setRedirectTo(pathTo('MessageEditScreen', {id: created_id}));
    }
    return () => {
      destroyForm();
    }
  }, [created_id, setRedirectTo, destroyForm]);

  // submit data handler
  const submitData = async (values) => {
    Logger.log('debug', `MessageForm.submitData(###)`);

    // API POST/PUT payload
    let payload = {};
    for (const input of Object.keys(data)) {
      if (input in values) {

        // transform profile refs to UUIDs
        if (['profile'].includes(input)) {
          payload['user_profile_id'] = values[input];
        }

        // transform entity refs to IDs
        else if (['care_guide'].includes(input)) {
          payload[input + '_id'] = values[input];
        }

        // transform optional values
        else if (['thread_id'].includes(input)) {
          if (values[input] !== '') {
            payload[input] = values[input];
          }
        }

        // transform boolean values
        else if (['is_draft'].includes(input)) {
          payload[input] = values[input] ? true : false;
        }

        else if (['sent_at'].includes(input)) {
          payload[input] = Format.date(values[input], Config.get('API_DATETIME_FORMAT'));
        }

        // handle recipients
        else if (input === 'recipients' && Array.isArray(values[input])) {
          payload[input] = values[input].map((x, i) => {
            const {id, key, profile, is_read, ...recipient} = x; // remove unused properties
            return {user_profile_id: profile.id, is_read: x['is_read'] ? true : false, ...recipient}
          });
        }

        else {
          payload[input] = values[input];
        }
      }
    }

    if (id) { // update
      props.update(id, payload, (success) => {
        if (success) {
          message.success(getI18n().t('feedback_form_success'));
        } else {
          message.error(getI18n().t('feedback_form_error'));
        }
      });      
    } else { // create
      props.create(payload, (success) => {
        if (success) {
          message.success(getI18n().t('feedback_form_success'));
        } else {
          message.error(getI18n().t('feedback_form_error'));
        }
      });      
    }
  }

  // form submit handler
  const handleFinish = async (values) => {
    Logger.log('debug', `MessageForm.handleFinish(###)`);
    if (!props.isSubmitting) {
      await submitData(values);
    }
  }

  // form error handler
  const handleFinishFailed = ({values, errorFields, outOfDate}) => {
    Logger.log('debug', `MessageForm.handleFinishFailed(###)`);
    message.error(getI18n().t('feedback_form_error'));
    if (errorFields && errorFields.length > 0) {
      form.scrollToField(errorFields[0].name);
    }
  }

  if (redirectTo) {
    return <Redirect to={redirectTo} />;
  }

  return (
    <Translation>{(t) => 
      <>
        <div className="message-form">
          <Form
            name="message_form"
            form={form}
            initialValues={data}
            onFinish={handleFinish}
            onFinishFailed={handleFinishFailed}
            validateTrigger="onSubmit"
            {...layout.main}
          >
            <Row gutter={16}>

              <Col xs={24} lg={18}>
                <Card
                  title={id ? t('message_edit_title') : t('message_add_title')}
                  extra={isLoading
                    ? <Spin indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />} />
                    : null}
                >

                  <div className="form-group">
                    <Form.Item
                      name="care_guide"
                      label={t('message_care_guide_id')}
                      rules={[
                        {required: true, message: t('feedback_validation_required')},
                      ]}
                    >
                      <Input
                        autoFocus
                        disabled={isLoading || isSubmitting}
                        onChange={e => setCareGuideId(e.target.value)}
                      />
                    </Form.Item>
                  </div>

                  <div className="form-group">
                    <Form.Item
                      name="thread_id"
                      label={t('message_thread_id')}
                      rules={null}
                    >
                      <Input disabled={isLoading || isSubmitting} />
                    </Form.Item>
                  </div>

                  <MembersInput
                    name="profile"
                    label={t('message_profile')}
                    careGuideIds={[careGuideId]}
                    rules={[
                      {required: true, message: t('feedback_validation_required')},
                    ]}
                  />

                  <Form.Item label={t('message_recipients')}>
                    <Form.List name="recipients">
                      
                      {(recipients, {add, remove, move}) => {
                        return(
                          <>
                            {recipients.map((recipient, i) => (
                              <RecipientInputRow
                                recipient={recipient}
                                careGuideId={careGuideId}
                                disabled={isLoading || isSubmitting}
                                remove={remove}
                                index={i}
                                key={i}
                              />
                            ))}

                            <Form.Item style={{marginBottom: 0}}>
                              <Button
                                type="dashed"
                                onClick={() => {
                                  add();
                                }}
                                block
                              >
                                <PlusOutlined /> Add recipient
                              </Button>
                            </Form.Item>
                          </>
                        );
                      }}
                    </Form.List>
                  </Form.Item>

                  <div className="form-group">
                    <Form.Item
                      name="sent_at"
                      label={t('message_sent_at')}
                    >
                      <DatePicker
                        showTime
                        disabled={isLoading || isSubmitting}
                        format={Config.get('DEFAULT_DATETIME_FORMAT')}
                      />
                    </Form.Item>
                  </div>

                  <div className="form-group">
                    <Form.Item
                      name="subject"
                      label={t('message_subject')}
                      rules={[
                        {type: 'string', min: 0, max: 250, message: t('feedback_validation_char_range', {min: 0, max: 250})},
                      ]}
                    >
                      <Input disabled={isLoading || isSubmitting} />
                    </Form.Item>
                  </div>

                  <BodyInput
                    name="body"
                    label={t('message_body')}
                    form={form}
                    defaultValue={form.getFieldValue(['body'])}
                    disabled={isLoading || isSubmitting}
                  />

                  <PublicStatusInput
                    name="public_status"
                    label={t('message_public_status')}
                    rules={[
                      {required: true, message: t('feedback_validation_required')},
                    ]}
                    disabled={isLoading || isSubmitting}
                  />

                  <div className="form-group">
                    <Form.Item
                      name="is_draft"
                      label={t('message_is_draft')}
                      valuePropName="checked"
                    >
                      <Checkbox disabled={isLoading || isSubmitting} />
                    </Form.Item>
                  </div>

                </Card>
              </Col>

              <Col xs={24} lg={6}>
                <Affix offsetTop={10}>
                  <Card title={t('form_metadata_header')}>
                    <FormMetadata
                      id={id}
                      isSubmitting={isSubmitting}
                      delete={props.delete.bind(this)}
                      deleteRedirectTo="MessagesScreen"
                      createdAt={props.createdAt}
                      updatedAt={props.updatedAt}
                    />
                  </Card>
                </Affix>
              </Col>

            </Row>
          </Form>
        </div>
      </>
    }</Translation>
  );
}

export default MessageForm;

Logger.log('silly', `MessageForm loaded.`);
